import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, Calendar, Boxes, Clock, Printer } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Tier 1 (Feb 2026): Manager "Depot Received" — read-only history of depot receipts.
export default function DepotReceived() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], total_received: 0 });
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/depot/received", { params: { days } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [days]);

  // Tier 2.1 (Feb 2026): Print Stock report (covers depot received as part of stock summary).
  const openPrint = async () => {
    try {
      const res = await api.get("/reports/stock.html", { params: { days, lang }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      const w = window.open(url, "_blank");
      if (!w) toast.error(lang === "ta" ? "பாப்-அப் தடுக்கப்பட்டது" : "Pop-up blocked");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(lang === "ta" ? "பதிவேற்ற முடியவில்ல" : "Print failed");
    }
  };

  const dispProd = (it) => (lang === "ta" && it.product_name_ta) ? it.product_name_ta : it.product_name;
  const dispDepot = (it) => (lang === "ta" && it.depot_name_ta) ? it.depot_name_ta : it.depot_name;

  return (
    <div className="min-h-screen bg-[#F5F5F7] page-slide-in pb-10">
      <TopBar title={t("depot_received") || "Depot Received"} onBack={() => nav("/manager")} />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        {/* Hero */}
        <div className="bg-gradient-to-r from-[#FF9500] to-[#CC7700] text-white rounded-3xl p-6 shadow-lg">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center"><Truck className="w-6 h-6" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">
                {lang === "ta" ? "டிப்போ-வுக்கு வந்த ஸ்டாக்" : "Depot Receipts"}
              </div>
              <div className="heading-font text-3xl font-bold">{data.total_received || 0} <span className="text-lg font-semibold opacity-90">{t("boxes")}</span></div>
              <div className="text-xs opacity-85 mt-0.5">
                {lang === "ta" ? `கடைசி ${days} நாட்களில்` : `last ${days} days`}
              </div>
            </div>
          </div>
        </div>

        {/* Range filter + Print */}
        <div className="flex flex-wrap gap-2 items-center" data-testid="dr-range-filters">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              data-testid={`dr-range-${d}`}
              onClick={() => setDays(d)}
              className={`px-4 py-2 rounded-xl text-sm font-bold ${days === d ? "bg-[#0055FF] text-white" : "bg-white text-[#1D1D1F] border border-gray-200 hover:border-[#0055FF]"}`}
            >
              <Calendar className="w-3.5 h-3.5 inline mr-1.5" />
              {lang === "ta" ? `${d} நாட்கள்` : `${d} days`}
            </button>
          ))}
          <button
            data-testid="dr-print-btn"
            onClick={openPrint}
            className="ml-auto px-4 py-2 rounded-xl text-sm font-bold bg-white text-[#0055FF] border border-[#0055FF] hover:bg-[#0055FF] hover:text-white flex items-center gap-2"
          >
            <Printer className="w-3.5 h-3.5" /> {t("print") || "Print"} / PDF
          </button>
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#86868B]">{t("loading")}</div>
          ) : data.items.length === 0 ? (
            <div className="p-10 text-center text-[#86868B]" data-testid="dr-empty">
              <Boxes className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <div className="font-bold">{lang === "ta" ? "பதிவு இல்ல" : "No receipts"}</div>
              <div className="text-xs mt-1">{lang === "ta" ? "இந்த காலத்தில் டிப்போ பெற்ற ஸ்டாக் இல்ல." : "No depot receipts in this range."}</div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.items.map((it, idx) => (
                <li key={it.id || idx} className="p-4 flex items-center gap-4" data-testid={`dr-item-${idx}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#FF9500]/10 grid place-items-center shrink-0">
                    <Truck className="w-5 h-5 text-[#FF9500]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold heading-font text-[#1D1D1F] truncate">{dispProd(it)}</div>
                    <div className="text-xs text-[#86868B] flex items-center gap-2 flex-wrap mt-0.5">
                      <span>{dispDepot(it)}</span>
                      {it.by_name && <span>• {it.by_name}</span>}
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(it.ts).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="heading-font text-xl font-bold text-[#34C759]">+{it.delta}</div>
                    <div className="text-[10px] text-[#86868B] uppercase tracking-wider font-bold">{t("boxes")}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
